import { useDroppable } from '@dnd-kit/core'
import {
  Paper,
  Text,
  Stack,
  Group,
  TextInput,
  ActionIcon,
  Badge,
  Box,
} from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import type { SeatingTable } from '@/lib/types'
import type { SeatingGroup } from '@/lib/seatingUtils'
import { getTablePersonCount } from '@/lib/seatingUtils'
import { GuestGroupCard } from './GuestGroupCard'
import styles from './SeatingChart.module.css'

interface SeatingTableCardProps {
  table: SeatingTable
  groups: SeatingGroup[]
  allGroups: SeatingGroup[]
  assignments: Record<string, string | null>
  onNameChange: (tableId: string, name: string) => void
  onRemove: (tableId: string) => void
  activeDragId: string | null
  onEditTestGuest?: (group: SeatingGroup) => void
  onDeleteTestGuest?: (group: SeatingGroup) => void
  onPromoteTestGuest?: (group: SeatingGroup) => void
}

export function SeatingTableCard({
  table,
  groups,
  allGroups,
  assignments,
  onNameChange,
  onRemove,
  activeDragId,
  onEditTestGuest,
  onDeleteTestGuest,
  onPromoteTestGuest,
}: SeatingTableCardProps) {
  const { setNodeRef, isOver } = useDroppable({ id: table.id })
  const personCount = getTablePersonCount(table.id, allGroups, assignments)

  return (
    <Paper
      ref={setNodeRef}
      p="sm"
      shadow="xs"
      withBorder
      className={`${styles.tableCard} ${isOver ? styles.dropTargetActive : ''}`}
    >
      <Group justify="space-between" mb={4} wrap="nowrap" align="center" gap={4}>
        <TextInput
          value={table.name}
          onChange={(e) => onNameChange(table.id, e.target.value)}
          size="xs"
          fw={600}
          className={styles.tableNameInput}
          aria-label="Tischname"
        />
        <Group gap={4} wrap="nowrap">
          <Badge size="lg" variant="filled" color="yellow" className={styles.tableCountBadge}>
            {personCount}
          </Badge>
          <ActionIcon
            size="sm"
            color="red"
            variant="subtle"
            onClick={() => onRemove(table.id)}
            aria-label="Tisch entfernen"
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      </Group>

      <Stack gap={4} className={styles.tableGuestList}>
        {groups.length === 0 ? (
          <Box className={styles.emptyDropZone}>
            <Text size="xs" c="dimmed" ta="center">
              Gäste hierher ziehen
            </Text>
          </Box>
        ) : (
          groups.map((group) => (
            <GuestGroupCard
              key={group.id}
              group={group}
              isDragging={activeDragId === group.id}
              onEditTestGuest={onEditTestGuest}
              onDeleteTestGuest={onDeleteTestGuest}
              onPromoteTestGuest={onPromoteTestGuest}
            />
          ))
        )}
      </Stack>
    </Paper>
  )
}
